import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import BadgeTradeDirection from 'src/components/Badge/BadgeTradeDirection';
import BadgeTradeRuleState from 'src/components/Badge/BadgeTradeRuleState';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import {
  TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL,
  TRADE_DIRECTION_UNSPECIFIED,
} from 'src/util/constants';
import { getNodesFromEdges } from 'src/util/graphql';
import { useIntlCurrency } from 'src/util/i18n/currency';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { valuePerUnitWithStrong } from 'src/util/i18n/energyPrice';
import buildTimeRangeSummary from 'src/util/i18n/timeRange';
import { buildTradePointTradeRuleTableLabel } from 'src/util/i18n/tradePoint';

import TradeRuleListTableItemClause from './TradeRuleListTableItemClause';

/**
 * GraphQL fragment for TradeRuleListTableItem
 */
const TradeRuleListTableItemFragment = graphql`
  fragment TradeRuleListTableItem_rule on TradeRule 
  @throwOnFieldError
  {
    uuid
    buyer {
      user {
        uuid
        email
        givenName
        familyName
      }
      community {
        uuid
        title
      }
      residualId
      tradePoint {
        uuid
        type
        meter {
          uuid
          identifier
          title
          property {
            uuid
            title
          }
        }
      }
    }
    seller {
      user {
        uuid
        email
        givenName
        familyName
      }
      community {
        uuid
        title
      }
      residualId
      tradePoint {
        uuid
        type
        meter {
          uuid
          identifier
          title
          property {
            uuid
            title
          }
        }
      }
    }
    clauses {
      edges {
        node {
          uuid
          price
          ignoreDaylightSavings
          ignorePublicHolidays
          timezone
          ...TradeRuleListTableItemClause_clause
        }
      }
    }
    tradeType
    state
    start
    finish
    proposedAt
    proposedBy {
      uuid
      email
      givenName
      familyName
    }
    acceptedAt
    acceptedBy {
      uuid
      email
      givenName
      familyName
    }
    rejectedAt
    rejectedBy {
      uuid
      email
      givenName
      familyName
    }
    cancelledAt
    cancelledBy {
      uuid
      email
      givenName
      familyName
    }
    closedAt
    closedBy {
      uuid
      email
      givenName
      familyName
    }
  }
`;

/**
 * Determine the trade direction relative to a given trade point.
 * @param {string} tradePointId - The trade point ID to compare against
 * @param {object} rule - The trade rule object containing buyer and seller data
 * @returns {string} Trade direction constant (BUY, SELL, or UNSPECIFIED)
 */
export const getDirection = (tradePointId, rule) => {
  const { buyer, seller } = rule;
  const buyerTradePointId = buyer?.tradePoint?.uuid ?? null;
  const sellerTradePointId = seller?.tradePoint?.uuid ?? null;

  if (buyerTradePointId && buyerTradePointId === tradePointId) {
    return TRADE_DIRECTION_BUY;
  }
  if (sellerTradePointId && sellerTradePointId === tradePointId) {
    return TRADE_DIRECTION_SELL;
  }
  return TRADE_DIRECTION_UNSPECIFIED;
};

/**
 * Get the counter-party trader (user, community, or residual) for a trade rule.
 * @param {string} tradePointId - The trade point ID to determine counter-party
 * @param {object} rule - The trade rule object containing buyer and seller data
 * @returns {object|null} Counter-party trader object or null if not found
 */
export const getCounterpartyTrader = (tradePointId, rule) => {
  const direction = getDirection(tradePointId, rule);

  if (direction === TRADE_DIRECTION_UNSPECIFIED) {
    return null;
  }

  const trader = direction === TRADE_DIRECTION_BUY
    ? rule.seller
    : rule.buyer;

  return trader?.user || trader?.community || trader?.residual || null;
};

/**
 * Get the counter-party trade point label for a trade rule.
 * @param {import('react-intl').IntlShape} intl - React Intl object for formatting
 * @param {string} tradePointId - The trade point ID to determine counter-party
 * @param {object} rule - The trade rule object containing buyer and seller data
 * @returns {React.ReactNode|null} Formatted trade point label component or null if not found
 */
export const getCounterpartyTradePoint = (intl, tradePointId, rule) => {
  const direction = getDirection(tradePointId, rule);

  if (direction === TRADE_DIRECTION_UNSPECIFIED) {
    return null;
  }

  const tradePoint = direction === TRADE_DIRECTION_BUY
    ? rule.seller?.tradePoint
    : rule.buyer?.tradePoint;

  const tradePointLabel = tradePoint ? buildTradePointTradeRuleTableLabel(intl, tradePoint) : null;
  return tradePointLabel;
};

/**
 * Renders a single row in the trade rule list table.
 * Displays trade rule information including type, state, direction, counter-party details,
 * pricing clauses, and validity period.
 * @param {object} props - Component props
 * @param {string} props.propertyId - The property ID for navigation links
 * @param {object} props.rule - Relay fragment ref for TradeRule
 * @param {string} props.tradePointId - The current trade point ID for direction calculation
 * @returns {React.ReactElement} Table row element with trade rule data
 */
function TradeRuleListTableItem({ propertyId, rule: ruleRef, tradePointId }) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();
  const rule = useFragment(TradeRuleListTableItemFragment, ruleRef);

  if (!rule) {
    return <Loading />;
  }

  const {
    uuid, tradeType, state, start, finish, clauses: clausesConnection,
  } = rule;

  const active = { start, finish };
  const clauses = getNodesFromEdges(clausesConnection).sort((a, b) => b.price - a.price);

  const counterPartyTradePoint = getCounterpartyTradePoint(intl, tradePointId, rule);
  const counterPartyTrader = getCounterpartyTrader(tradePointId, rule);
  const direction = getDirection(tradePointId, rule);

  const copyTradeRuleIdTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.copy_title', defaultMessage: 'Copy trade rule id to the clipboard' });
  const copyTradeRuleIdSuccessMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.copy.success.message', defaultMessage: 'Trade rule id copied to the clipboard.' });
  const copyCounterpartyIdTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.copy_counterparty_id_title', defaultMessage: 'Copy counterparty id to the clipboard' });
  const copyCounterpartySuccessMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_list_table.copy_counterparty_id.success.message', defaultMessage: 'Counterparty id copied to the clipboard' });

  return (
    <tr key={`trade-rule-${uuid}`}>
      <th>
        <Link to={`/properties/${propertyId}/trade-rules/${uuid}`}>
          <UUID
            copyTitle={copyTradeRuleIdTitle}
            copySuccessMsg={copyTradeRuleIdSuccessMsg}
            uuid={uuid}
            url={`/properties/${propertyId}/trade-rules/${uuid}`}
          />
        </Link>
      </th>
      <td>
        <BadgeTradeType type={tradeType} />
      </td>
      <td>
        <BadgeTradeRuleState state={state} />
      </td>
      <td>
        <BadgeTradeDirection direction={direction} />
      </td>
      <td>
        {counterPartyTrader && (
        <>
          <strong>{displayUserName(intl, counterPartyTrader)}</strong>
          <br />
          <UUID
            copyTitle={copyCounterpartyIdTitle}
            copySuccessMsg={copyCounterpartySuccessMsg}
            uuid={counterPartyTrader.uuid}
          />
        </>
        )}
      </td>
      <td>
        {counterPartyTradePoint}
      </td>
      <td>
        {clauses.length === 0
            && (
            <p style={{ display: 'flex' }}>
              {valuePerUnitWithStrong(intl, intlCurrency, undefined)}
            </p>
            )}
        {clauses.length > 0
          && (
          <ul className="ps-2">
            {clauses?.map((clause) => (
              <TradeRuleListTableItemClause
                clause={clause}
                key={clause.uuid}
                showConditions={false}
              />
            ))}
          </ul>
          )}
      </td>
      <td data-testid="validity">
        {buildTimeRangeSummary(intl, active)}
      </td>
    </tr>
  );
}

TradeRuleListTableItem.propTypes = {
  propertyId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  rule: PropTypes.object.isRequired, // Relay fragment ref
  tradePointId: PropTypes.string.isRequired,
};

export default TradeRuleListTableItem;
